import { assign, createMachine } from "xstate";
import { setMnpMode } from "@nju/data-access-portal-msisdn";
import { HttpError } from "@nju/result";
import { IScope } from "@nju/scope";

export type ISelectedPostpaidDay = "EOP" | "DAY";

interface IContext {
  variant?: IScope;
  orchestratorId: string;
  selectedDate?: ISelectedPostpaidDay;
  errorMessage?: string;
  isLastNumberOnboarded: boolean;
}

type IEvent =
  | { type: "SET_POSTPAID_DATE"; value: ISelectedPostpaidDay }
  | { type: "SUBMIT" }
  | { type: "SUCCESS" }
  | { type: "FAILED" }
  | { type: "ANOTHER_ORDER_EXISTS" };

export const postpaidMigrationMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "init",
    states: {
      init: {
        on: {
          SET_POSTPAID_DATE: {
            actions: "setMsisdnOfferType",
          },
          SUBMIT: [
            {
              cond: "isEopSelected",
              target: "setMnpMode",
            },
            {
              cond: "isDaySelected",
              actions: "redirectToPostpaidDayPage",
              target: "goToNextStep",
            },
            {
              actions: "setErrorMessage",
            },
          ],
        },
      },
      setMnpMode: {
        invoke: {
          src: "setMnpMode",
        },
        on: {
          SUCCESS: {
            target: "resolveNextStep",
          },
          FAILED: {
            target: "error",
          },
        },
      },
      resolveNextStep: {
        always: [
          {
            target: "redirectToNextMultiOrderStep",
          },
        ],
      },
      redirectToNextMultiOrderStep: {
        always: [
          {
            cond: "isLastNumberCurrentlyOnboarded",
            actions: "redirectToDeliveryStep",
            target: "goToNextStep",
          },
          {
            actions: "redirectToProceedFurtherStep",
            target: "goToNextStep",
          },
        ],
      },
      goToNextStep: {
        type: "final",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    actions: {
      setMsisdnOfferType: assign((context, event: IEvent) => {
        if (event.type === "SET_POSTPAID_DATE") {
          return {
            selectedDate: event.value,
            errorMessage: undefined,
          };
        }

        return context;
      }),
      setErrorMessage: assign((context, _: IEvent) => {
        return {
          errorMessage:
            context.variant !== "sales"
              ? "Aby przejść dalej, wybierz, co chcesz zrobić."
              : "Aby przejść dalej, wybierz, co chce zrobić klient.",
        };
      }),
    },
    guards: {
      isEopSelected: ({ selectedDate }) => {
        return selectedDate === "EOP";
      },
      isDaySelected: ({ selectedDate }) => {
        return selectedDate === "DAY";
      },
      isLastNumberCurrentlyOnboarded: (context) => {
        return context.isLastNumberOnboarded;
      },
    },
    services: {
      setMnpMode: ({ orchestratorId }) => {
        return async (callback) => {
          try {
            await setMnpMode({
              portingMode: "EOP",
              orchestratorId,
              sourceContractType: "POSTPAID",
            });

            return callback({ type: "SUCCESS" });
          } catch (error) {
            if (
              error instanceof HttpError &&
              error.code === "ANOTHER_ORDER_IN_PROGRESS"
            ) {
              return callback({ type: "ANOTHER_ORDER_EXISTS" });
            }

            return callback({ type: "FAILED" });
          }
        };
      },
    },
  },
);
